import { EMapProvider, IAndroidConfig, IApiConfig, IWebConfig } from "@kuika/kuika-cl-model";

/**
 * The config sections that carry a `mapProvider`. Sections are optional/nullable because callers
 * pass antd form values, which only contain the sections the surface actually renders, and `IConfig`
 * itself declares the mobile sections as nullable.
 */
export interface IMapProviderSections {
  apiConfig?: IApiConfig | null;
  webConfig?: IWebConfig | null;
  androidConfig?: IAndroidConfig | null;
}

/**
 * Returns a copy of `config` with every present section's absent `mapProvider` set to Google Maps.
 *
 * An absent `mapProvider` means Google Maps (existing configs predate the field). Settings surfaces
 * hash their form values to detect unsaved changes; without this normalization, merely rendering the
 * provider selector on such a config — or explicitly picking the already-effective Google Maps —
 * would be reported as a change. Callers must normalize both the baseline and the comparison hash.
 */
export const normalizeMapProviderDefaults = <T extends IMapProviderSections>(config: T): T => {
  const normalized: T = { ...config };

  if (normalized.apiConfig) {
    normalized.apiConfig = {
      ...normalized.apiConfig,
      mapProvider: normalized.apiConfig.mapProvider ?? EMapProvider.GoogleMaps
    };
  }
  if (normalized.webConfig) {
    normalized.webConfig = {
      ...normalized.webConfig,
      mapProvider: normalized.webConfig.mapProvider ?? EMapProvider.GoogleMaps
    };
  }
  if (normalized.androidConfig) {
    normalized.androidConfig = {
      ...normalized.androidConfig,
      mapProvider: normalized.androidConfig.mapProvider ?? EMapProvider.GoogleMaps
    };
  }

  return normalized;
};
